/**
 * ONE shared pricing schema used by BOTH the charger default-tariff editor
 * (views/TariffEditor.tsx) and the Pricing Profiles create/edit form
 * (pages/PricingProfilesPage.tsx). The only difference between the two flows is where
 * the resulting pricebook is saved — the form, fields, validation, serialization and
 * canonical pricebook shape are identical here.
 *
 * Canonical write pricebook: { currency_code, effective_pricebook: RateRuleWrite[] } -
 * exactly the shape the backend tariff/profile endpoints consume. A rate is the exact
 * decimal string the operator sees or the server sent; it never passes through a float.
 */
import type { FeeType, Uom, PricebookWrite, RateRuleWrite } from "@/lib/api";
import { compareDecimal, isNewInputRateAmount, parseDecimalInput } from "@voltie/localization-web";
import { localization, type Translate } from "@/app/localization";

/** What the server sent for a saved rule's rate: its exact amount, or stored text it could not read. */
export type SavedRate =
  | { readonly kind: "exact"; readonly amount: string; readonly currency: string }
  | { readonly kind: "unreadable"; readonly raw: string };

/** One editable rule. `rate` is the text of the rate field ("" or "0" is free). */
export interface PricingRuleValues {
  fee_type: FeeType;
  uom: Uom;
  rate: string;
  is_tou?: boolean;
  start_time?: string;
  end_time?: string;
  days?: string;
  priority?: number;
  /** Set for a rule loaded from a saved tariff. */
  saved?: SavedRate;
}

export interface PricingFormValues {
  /** ISO code: the saved tariff's own, or the tenant's operating currency for new prices; "" while unknown. Never chosen by the operator. */
  currency: string;
  rules: PricingRuleValues[];
}

// fee_type → allowed units of measure (the canonical set from the charger tariff form).
export const UOM_BY_FEE: Record<FeeType, Uom[]> = {
  charging: ["kwh", "hour"],
  idle: ["hour"],
  activation: ["session"],
};

/** A new tariff or profile: no rules and no currency until the tenant's is known (lib/moneyManagement). */
export function emptyPricingForm(): PricingFormValues {
  return { currency: "", rules: [] };
}

export function newRule(): PricingRuleValues {
  return { fee_type: "charging", uom: "kwh", rate: "0", is_tou: false,
           start_time: "", end_time: "", days: "", priority: 0 };
}

type RateValue = { ok: true; value: string } | { ok: false; error: "negative" | "invalid" | "unreadable" };

/**
 * The exact rate a rule writes. An unchanged saved rate is sent as the server sent it;
 * an empty field is free ("0", as its placeholder says); anything else must be a plain
 * decimal and is sent in canonical form, never rounded.
 */
export function ruleRate(rule: PricingRuleValues): RateValue {
  if (rule.saved?.kind === "unreadable") return { ok: false, error: "unreadable" };
  if (rule.saved?.kind === "exact" && rule.rate === rule.saved.amount) return { ok: true, value: rule.saved.amount };
  if (rule.rate.trim() === "") return { ok: true, value: "0" };
  const parsed = parseDecimalInput(rule.rate, { maxFractionDigits: 30, allowGrouping: false });
  if (parsed.ok) return { ok: true, value: parsed.value };
  return { ok: false, error: parsed.error === "negative" ? "negative" : "invalid" };
}

/** A saved rate that is unchanged and has more digits than a new rate may have (the server refuses it). */
export function savedRateTooPrecise(rule: PricingRuleValues): boolean {
  return rule.saved?.kind === "exact" && rule.rate === rule.saved.amount && !isNewInputRateAmount(rule.saved.amount);
}

/** Free or paid as currently edited; null while a rate is unreadable or not a number. */
export function formPricingMode(v: PricingFormValues): "free" | "paid" | null {
  let paid = false;
  for (const rule of v.rules) {
    const rate = ruleRate(rule);
    if (!rate.ok) return null;
    if (compareDecimal(rate.value, "0") > 0) paid = true;
  }
  return paid ? "paid" : "free";
}

/** Serialize validated form values to the canonical pricebook the backend stores (identical
 *  for the charger default tariff and a pricing profile). Rates are exact decimal strings. */
export function valuesToPricebook(v: PricingFormValues): PricebookWrite {
  return {
    currency_code: v.currency,
    effective_pricebook: v.rules.map((r, i) => {
      const rate = ruleRate(r);
      if (!rate.ok) throw new Error(`rule ${i + 1} has no writable rate`);
      const base: RateRuleWrite = { fee_type: r.fee_type, uom: r.uom, rate_base: rate.value };
      if (r.is_tou) {
        base.is_tou = true;
        base.start_time = r.start_time || "";
        base.end_time = r.end_time || "";
        base.days = r.days || "";
        base.priority = r.priority ?? 0;
      }
      return base;
    }),
  };
}

function timeToMinutes(t?: string): number | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  if (!m) return null;
  const h = +m[1], min = +m[2];
  if (h < 0 || h > 24 || min < 0 || min > 59 || (h === 24 && min !== 0)) return null;
  return h * 60 + min;
}

/** Identical validation for both flows. Returns an error message (in the active
 *  catalog language at call time) or null. Digit limits and rate caps of new input
 *  are the server's; a saved rate it would refuse is flagged by savedRateTooPrecise. */
export function validatePricingForm(v: PricingFormValues, t: Translate = localization.t): string | null {
  if (!v.currency || !v.currency.trim()) return t("billing.tariffs.error_currency_required");
  for (let i = 0; i < v.rules.length; i++) {
    const r = v.rules[i];
    const rule = i + 1;
    if (r.saved?.kind === "unreadable") return t("billing.tariffs.rule_unreadable", { raw: r.saved.raw });
    if (!UOM_BY_FEE[r.fee_type]?.includes(r.uom)) return t("billing.tariffs.error_invalid_unit", { rule, fee: r.fee_type });
    const rate = ruleRate(r);
    if (!rate.ok) return t(rate.error === "negative" ? "billing.tariffs.error_rate_negative" : "billing.tariffs.error_rate_invalid", { rule });
    if (r.is_tou) {
      const s = timeToMinutes(r.start_time), e = timeToMinutes(r.end_time);
      if (s === null || e === null) return t("billing.tariffs.error_window_invalid", { rule });
      // To earlier than From is a valid overnight window (backend RuleMatcher wraps past
      // midnight, e.g. 22:00–06:00). Only a zero-length window is an error.
      if (e === s) return t("billing.tariffs.error_window_empty", { rule });
    }
  }
  // No two all-day (non-ToU) rules for the same fee+uom (they'd both always apply).
  const seen = new Set<string>();
  for (let i = 0; i < v.rules.length; i++) {
    const r = v.rules[i];
    if (r.is_tou) continue;
    const key = `${r.fee_type}|${r.uom}`;
    if (seen.has(key)) return t("billing.tariffs.error_all_day_conflict", { fee: r.fee_type });
    seen.add(key);
  }
  return null;
}

// "<fee>|<uom>" -> the rate enum of billing.tariffs.summary_rate.
export const SUMMARY_RATE: Record<string, string> = {
  "charging|kwh": "charging_kwh",
  "charging|hour": "charging_hour",
  "idle|hour": "idle_hour",
  "activation|session": "activation_session",
};
export const SUMMARY_ORDER = ["charging|kwh", "charging|hour", "idle|hour", "activation|session"];
